import { useContext, useEffect, useMemo, useState } from "react";
import { Button, Input, Select } from "../../../ui";
import { Context } from "../../../../context/Context";
import { useAccess } from "../../../../access/hooks/useAccess";
import { formatSar } from "../../../../utils/billingMoney";
import {
  listBillingPlansAPI,
  previewPlanChangeAPI,
  schedulePlanChangeAPI,
} from "../../../../utils/api/billing";
import { labelFor, useBillingAdminText } from "../billingAdminI18n";

const formatDate = (value, lang) => {
  if (!value) return "-";
  return new Intl.DateTimeFormat(lang === "ar" ? "ar-SA" : "en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    timeZone: "UTC",
  }).format(new Date(value));
};

const ChangePlanModal = ({ subscription, onCancel, onChanged }) => {
  const { lang } = useContext(Context);
  const { can } = useAccess();
  const text = useBillingAdminText(lang);
  const token = localStorage.getItem("auth-token");
  const [plans, setPlans] = useState([]);
  const [planId, setPlanId] = useState("");
  const [branchIds, setBranchIds] = useState(
    (subscription.branchCoverage || []).map((branch) => branch.branchId),
  );
  const [reason, setReason] = useState("");
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const result = await listBillingPlansAPI(token, {
          status: "active",
          limit: 100,
        });
        setPlans(result.data || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const selectedPlan = useMemo(
    () => plans.find((plan) => plan._id === planId),
    [plans, planId],
  );
  const body = { targetPlanId: planId, branchIds, reason };

  const loadPreview = async () => {
    if (!planId) {
      setError(text.subscriptions.selectPlan);
      return;
    }
    setPreviewing(true);
    setError("");
    try {
      const result = await previewPlanChangeAPI(token, subscription._id, body);
      setPreview(result.data);
    } catch (err) {
      setPreview(null);
      setError(err.message);
    } finally {
      setPreviewing(false);
    }
  };

  const confirm = async () => {
    if (!can("billing.subscription.change")) return;
    if (saving) return;
    setSaving(true);
    setError("");
    try {
      await schedulePlanChangeAPI(token, subscription._id, body);
      await onChanged();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  const toggleBranch = (branchId) => {
    setPreview(null);
    setBranchIds((current) =>
      current.includes(branchId)
        ? current.filter((item) => item !== branchId)
        : [...current, branchId],
    );
  };

  return (
    <div className="billing-modal-backdrop">
      <div className="billing-modal">
        <h3>{text.subscriptions.changePlanTitle}</h3>
        <p>{text.subscriptions.changePlanDescription}</p>
        {error && <div className="billing-error">{error}</div>}
        {loading ? (
          <div className="billing-loading">{text.common.loading}</div>
        ) : (
          <div className="billing-plan-modal-form">
            <div className="billing-preview-box">
              <div>
                <span>{text.subscriptions.currentPlan}</span>
                <strong>
                  {subscription.planSnapshot?.code} v
                  {subscription.planSnapshot?.version}
                </strong>
              </div>
              <div>
                <span>{text.subscriptions.currentBillingCycle}</span>
                <strong>
                  {labelFor(
                    text.cycles,
                    subscription.planSnapshot?.billingCycle,
                  )}
                </strong>
              </div>
            </div>
            <Select
              label={text.subscriptions.futurePlan}
              value={planId}
              options={plans.map((plan) => ({
                value: plan._id,
                label: `${plan.code} v${plan.version} - ${plan.nameEn} - ${formatSar(plan.basePriceMinor, lang)}`,
              }))}
              onChange={(event) => {
                setPlanId(event.target.value);
                setPreview(null);
              }}
              placeholder={text.subscriptions.selectPlan}
            />
            {selectedPlan && (
              <div className="billing-preview-box">
                <strong>
                  {selectedPlan.code} v{selectedPlan.version}
                </strong>
                <span>
                  {labelFor(text.cycles, selectedPlan.billingCycle)} |{" "}
                  {text.subscriptions.includedBranches}:{" "}
                  {selectedPlan.includedBranchCount}
                </span>
              </div>
            )}
            <div className="billing-branch-list">
              <strong>{text.subscriptions.branchCoverage}</strong>
              {(subscription.branchCoverage || []).map((branch) => (
                <label key={branch.branchId} className="billing-branch-option">
                  <input
                    type="checkbox"
                    checked={branchIds.includes(branch.branchId)}
                    onChange={() => toggleBranch(branch.branchId)}
                  />
                  <span>{branch.branchName}</span>
                </label>
              ))}
            </div>
            <Input
              label={text.common.reason}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder={text.subscriptions.changeReasonPlaceholder}
            />
            <Button
              appearance="outline"
              loading={previewing}
              onClick={loadPreview}
            >
              {text.subscriptions.previewChange}
            </Button>
            {preview?.pricingPreview && (
              <div className="billing-preview-box">
                <div>
                  <span>{text.subscriptions.effectiveDate}</span>
                  <strong>{formatDate(preview.effectiveAt, lang)}</strong>
                </div>
                <div>
                  <span>{text.subscriptions.futureBillingCycle}</span>
                  <strong>
                    {labelFor(text.cycles, preview.futurePlan?.billingCycle)}
                  </strong>
                </div>
                <div>
                  <span>{text.subscriptions.selectedBranches}</span>
                  <strong>{preview.pricingPreview.coveredBranchCount}</strong>
                </div>
                <div>
                  <span>{text.subscriptions.extraBranches}</span>
                  <strong>{preview.pricingPreview.extraBranchCount}</strong>
                </div>
                <div>
                  <span>{text.common.subtotal}</span>
                  <strong>
                    {formatSar(preview.pricingPreview.subtotalMinor, lang)}
                  </strong>
                </div>
                <div>
                  <span>{text.common.vat}</span>
                  <strong>
                    {formatSar(preview.pricingPreview.vatAmountMinor, lang)}
                  </strong>
                </div>
                <div>
                  <span>{text.common.total}</span>
                  <strong>
                    {formatSar(preview.pricingPreview.totalMinor, lang)}
                  </strong>
                </div>
                <p>{text.subscriptions.noProration}</p>
              </div>
            )}
            <div className="billing-modal-actions">
              <Button
                appearance="filled"
                loading={saving}
                disabled={!preview}
                onClick={confirm}
              >
                {text.subscriptions.scheduleChange}
              </Button>
              <Button appearance="ghost" onClick={onCancel}>
                {text.common.cancel}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChangePlanModal;
